/**
 * Converts a UUID string into a consistent hex color
 * @param uuid - The UUID string to convert
 * @returns A hex color string (e.g., "#FF5733")
 */
export function uuidToHexColor(uuid: string): string {
  // Remove hyphens and convert to lowercase for consistent processing
  const cleanUuid = uuid.replace(/-/g, '').toLowerCase();
  
  // Ensure we have a valid UUID format (32 hex characters)
  if (!/^[0-9a-f]{32}$/.test(cleanUuid)) {
    throw new Error('Invalid UUID format');
  }
  
  // Extract RGB values from different parts of the UUID
  // Use different segments to ensure good color distribution
  const r1 = parseInt(cleanUuid.substring(0, 2), 16);
  const r2 = parseInt(cleanUuid.substring(8, 10), 16);
  const red = Math.floor((r1 + r2) / 2);
  
  const g1 = parseInt(cleanUuid.substring(16, 18), 16);
  const g2 = parseInt(cleanUuid.substring(24, 26), 16);
  const green = Math.floor((g1 + g2) / 2);
  
  const b1 = parseInt(cleanUuid.substring(32, 34), 16) || parseInt(cleanUuid.substring(30, 32), 16);
  const b2 = parseInt(cleanUuid.substring(6, 8), 16);
  const blue = Math.floor((b1 + b2) / 2);
  
  // Convert to hex and ensure 2-digit format
  const toHex = (value: number): string => {
    const hex = value.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
}

/**
 * Converts a UUID string into a consistent hex color with better color distribution
 * This version ensures more vibrant colors by avoiding very dark colors
 * @param uuid - The UUID string to convert
 * @returns A hex color string (e.g., "#FF5733")
 */
export function uuidToVibrantHexColor(uuid: string): string {
  // Remove hyphens and convert to lowercase for consistent processing
  const cleanUuid = uuid.replace(/-/g, '').toLowerCase();
  
  // Ensure we have a valid UUID format (32 hex characters)
  if (!/^[0-9a-f]{32}$/.test(cleanUuid)) {
    throw new Error('Invalid UUID format');
  }
  
  // Extract RGB values with better distribution
  const red = parseInt(cleanUuid.substring(0, 2), 16);
  const green = parseInt(cleanUuid.substring(8, 10), 16);
  const blue = parseInt(cleanUuid.substring(16, 18), 16);
  
  // Ensure minimum brightness to avoid very dark colors
  const minBrightness = 80;
  const maxBrightness = 255;
  
  // Adjust colors to ensure good visibility
  const adjustedRed = Math.max(minBrightness, Math.min(maxBrightness, red + 50));
  const adjustedGreen = Math.max(minBrightness, Math.min(maxBrightness, green + 50));
  const adjustedBlue = Math.max(minBrightness, Math.min(maxBrightness, blue + 50));
  
  // Convert to hex and ensure 2-digit format
  const toHex = (value: number): string => {
    const hex = value.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(adjustedRed)}${toHex(adjustedGreen)}${toHex(adjustedBlue)}`;
}

/**
 * Converts a UUID string into a consistent HSL color
 * @param uuid - The UUID string to convert
 * @returns An HSL color string (e.g., "hsl(120, 50%, 50%)")
 */
export function uuidToHslColor(uuid: string): string {
  // Remove hyphens and convert to lowercase for consistent processing
  const cleanUuid = uuid.replace(/-/g, '').toLowerCase();
  
  // Ensure we have a valid UUID format (32 hex characters)
  if (!/^[0-9a-f]{32}$/.test(cleanUuid)) {
    throw new Error('Invalid UUID format');
  }
  
  // Extract hue, saturation, and lightness from UUID
  const hue = parseInt(cleanUuid.substring(0, 3), 16) % 360;
  const saturation = 40 + (parseInt(cleanUuid.substring(8, 10), 16) % 40); // 40-80%
  const lightness = 40 + (parseInt(cleanUuid.substring(16, 18), 16) % 30); // 40-70%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

/**
 * Utility function to get a contrasting text color (black or white) for a given hex color
 * @param hexColor - The hex color to get contrast for
 * @returns Either "#000000" or "#FFFFFF" for optimal contrast
 */
export function getContrastColor(hexColor: string): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black for light backgrounds, white for dark backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
